export type ErrorCategory =
  | 'ValidationError'
  | 'AuthenticationError'
  | 'AuthorizationError'
  | 'NotFound'
  | 'Conflict'
  | 'RateLimited'
  | 'Timeout'
  | 'NetworkError'
  | 'ServiceError'
  | 'UnknownError';

export interface FieldError {
  field: string;
  message: string;
}

export class AppError extends Error {
  constructor(
    public readonly category: ErrorCategory,
    message: string,
    public readonly statusCode?: number,
    public readonly fieldErrors?: FieldError[],
    /** Backend error-code index entry (docs/ERROR_CODES.md), e.g. SYS-0001. */
    public readonly code?: string,
    /** Request trace id from X-Request-Id; support searches the logs with it. */
    public readonly traceId?: string,
  ) {
    super(message);
    this.name = 'AppError';
  }

  /** "SYS-0001, ref 8f3a2c1d" for showing next to the message; empty when neither is known. */
  get reference(): string {
    const parts = [this.code, this.traceId ? `ref ${this.traceId.slice(0, 8)}` : undefined];
    return parts.filter(Boolean).join(', ');
  }
}

/** Error code and trace id from response headers, falling back to the body. */
export interface ErrorMeta {
  code?: string;
  traceId?: string;
}

export function errorMeta(headers: unknown, body: unknown): ErrorMeta {
  const h = (headers ?? {}) as Record<string, unknown>;
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  return {
    code: str(h['x-error-code']) ?? str(b['code']),
    traceId: str(h['x-request-id']) ?? str(b['traceId']),
  };
}

export function categorizeHttpError(status: number, body?: unknown, meta: ErrorMeta = {}): AppError {
  const msg = extractMessage(body);
  const make = (category: ErrorCategory, message: string, fields?: FieldError[]) =>
    new AppError(category, message, status, fields, meta.code, meta.traceId);
  if (status === 400) return make('ValidationError', msg || 'Validation failed', extractFieldErrors(body));
  if (status === 401) return make('AuthenticationError', msg || 'Session expired. Please log in again.');
  if (status === 403) return make('AuthorizationError', 'You do not have permission to perform this action.');
  if (status === 404) return make('NotFound', msg || 'The requested resource was not found.');
  if (status === 409) return make('Conflict', msg || 'This operation conflicts with existing data.');
  if (status === 429) return make('RateLimited', 'Too many requests. Please wait and try again.');
  if (status >= 500) {
    const ref = new AppError('ServiceError', '', status, undefined, meta.code, meta.traceId).reference;
    return make('ServiceError', `A server error occurred. Please try again later.${ref ? ` (${ref})` : ''}`);
  }
  return make('UnknownError', msg || 'An unexpected error occurred.');
}

function extractMessage(body: unknown): string {
  if (!body || typeof body !== 'object') return '';
  const b = body as Record<string, unknown>;
  const nested = b['error'] as Record<string, unknown> | null | undefined;
  return (
    (b['Resp_desc'] as string) ||
    (b['message'] as string) ||
    (nested?.['error'] as string) ||
    (nested?.['message'] as string) ||
    ''
  );
}

function extractFieldErrors(body: unknown): FieldError[] {
  if (!body || typeof body !== 'object') return [];
  const b = body as Record<string, unknown>;
  if (Array.isArray(b['errors'])) {
    return (b['errors'] as Array<{ field: string; message: string }>).map(e => ({
      field: e.field,
      message: e.message,
    }));
  }
  return [];
}
